import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { format } from 'date-fns';
import {
  Hotel,
  Calendar,
  MapPin,
  LogOut,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Plus,
  ArrowRight,
  Loader2,
  FileText,
  Bell,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';

export default function GuestDashboardPage() {
  const navigate = useNavigate();
  const { token, guest, isAuthenticated, logout, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }
    loadData();
  }, [isAuthenticated, authLoading, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reservationsRes, requestsRes] = await Promise.all([
        guestPortalApi.listReservations(token),
        guestPortalApi.listServiceRequests(token),
      ]);
      setReservations(reservationsRes.data || []);
      setServiceRequests(requestsRes.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error.response?.status === 401) {
        logout();
        navigate('/portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/portal');
  };

  const getStatusBadge = (status) => {
    const styles = {
      CONFIRMED: 'bg-success/10 text-success',
      CHECKED_IN: 'bg-primary/10 text-primary',
      CHECKED_OUT: 'bg-muted text-muted-foreground',
      CANCELLED: 'bg-destructive/10 text-destructive',
      NO_SHOW: 'bg-warning/10 text-warning',
      PENDING: 'bg-warning/10 text-warning',
      COMPLETED: 'bg-success/10 text-success',
      IN_PROGRESS: 'bg-primary/10 text-primary',
    };
    return <Badge className={styles[status] || 'bg-muted'}>{status?.replace('_', ' ')}</Badge>;
  };

  const upcomingReservations = reservations.filter(r => 
    ['CONFIRMED', 'GUARANTEED'].includes(r.status) && new Date(r.arrival_date) >= new Date()
  );
  
  const pastReservations = reservations.filter(r => 
    ['CHECKED_OUT', 'CANCELLED', 'NO_SHOW'].includes(r.status) || new Date(r.departure_date) < new Date()
  );

  const activeReservations = reservations.filter(r => r.status === 'CHECKED_IN');

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="guest-dashboard-page">
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Welcome back, {guest?.first_name}!</h1>
          <p className="text-muted-foreground">Manage your reservations and service requests</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/portal')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">New Booking</p>
                  <p className="text-sm text-muted-foreground">Search & book</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/portal/requests/new')}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-chart-2/10">
                  <Bell className="h-6 w-6 text-chart-2" />
                </div>
                <div>
                  <p className="font-medium">Service Request</p>
                  <p className="text-sm text-muted-foreground">Request help</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-success/10">
                  <Calendar className="h-6 w-6 text-success" />
                </div>
                <div>
                  <p className="font-medium">{upcomingReservations.length}</p>
                  <p className="text-sm text-muted-foreground">Upcoming stays</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-warning/10">
                  <FileText className="h-6 w-6 text-warning" />
                </div>
                <div>
                  <p className="font-medium">{serviceRequests.filter(r => r.status !== 'COMPLETED').length}</p>
                  <p className="text-sm text-muted-foreground">Open requests</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Current Stay Banner */}
        {activeReservations.length > 0 && (
          <Card className="mb-8 border-primary/50 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-primary" />
                You're Currently Checked In
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeReservations.map(res => (
                <div key={res.id} className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{res.properties?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Room: {res.rooms?.room_number || 'TBA'} · Check-out: {format(new Date(res.departure_date), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigate(`/portal/reservations/${res.id}`)}>
                      View Details
                    </Button>
                    <Button size="sm" onClick={() => navigate(`/portal/reservations/${res.id}/folio`)}>
                      View Folio
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="upcoming" className="space-y-6">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming ({upcomingReservations.length})</TabsTrigger>
            <TabsTrigger value="past">Past Stays ({pastReservations.length})</TabsTrigger>
            <TabsTrigger value="requests">Service Requests ({serviceRequests.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {upcomingReservations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No Upcoming Reservations</p>
                  <p className="text-muted-foreground mb-4">Plan your next stay with us</p>
                  <Button onClick={() => navigate('/portal')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Book Now
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {upcomingReservations.map(res => (
                  <Card key={res.id} className="hover:shadow-md transition-shadow" data-testid={`reservation-${res.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <div className="p-3 rounded-lg bg-muted">
                            <Hotel className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{res.properties?.name || 'Hotel'}</h3>
                            <p className="text-sm text-muted-foreground flex items-center gap-1">
                              <MapPin className="h-4 w-4" />
                              {res.properties?.address_city || 'City'}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-sm">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                {format(new Date(res.arrival_date), 'MMM d')} - {format(new Date(res.departure_date), 'MMM d, yyyy')}
                              </span>
                              <span>{res.nights} night(s)</span>
                            </div>
                            <p className="text-sm mt-1">
                              Confirmation: <span className="font-mono">{res.confirmation_number}</span>
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(res.status)}
                          <p className="text-2xl font-bold mt-2">${res.total_rate?.toFixed(2)}</p>
                          <div className="flex gap-2 mt-4">
                            <Button variant="outline" size="sm" onClick={() => navigate(`/portal/reservations/${res.id}/pre-checkin`)}>
                              Pre-Check-In
                            </Button>
                            <Button size="sm" onClick={() => navigate(`/portal/reservations/${res.id}`)}>
                              Details
                              <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {pastReservations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No Past Stays</p>
                  <p className="text-muted-foreground">Your stay history will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pastReservations.map(res => (
                  <Card key={res.id} className="opacity-75 hover:opacity-100 transition-opacity">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex gap-4">
                          <div className="p-3 rounded-lg bg-muted">
                            <Hotel className="h-8 w-8 text-muted-foreground" />
                          </div>
                          <div>
                            <h3 className="font-semibold">{res.properties?.name || 'Hotel'}</h3>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(res.arrival_date), 'MMM d')} - {format(new Date(res.departure_date), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(res.status)}
                          <Button variant="ghost" size="sm" className="mt-2" onClick={() => navigate('/portal')}>
                            Book Again
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="requests">
            <div className="flex justify-end mb-4">
              <Button onClick={() => navigate('/portal/requests/new')} data-testid="new-request-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Request
              </Button>
            </div>
            {serviceRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No Service Requests</p>
                  <p className="text-muted-foreground">Need assistance during your stay? Submit a request.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {serviceRequests.map(req => (
                  <Card key={req.id} data-testid={`request-${req.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">{req.title}</h3>
                            {getStatusBadge(req.status)}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {req.category} · {req.properties?.name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Submitted: {format(new Date(req.created_at), 'MMM d, yyyy h:mm a')}
                          </p>
                        </div>
                        <Badge variant="outline">{req.priority}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
